// The backend runs on UTC (`src/config/app.php` → 'timezone' => 'UTC') and
// serialises its timestamps as a zone-less "dd/MM/yyyy HH:mm" string. Nothing in
// that string says it is UTC, so printing it straight to the screen shows an
// order placed at 15:00 as 13:00 — and an order placed just after midnight ends
// up on the previous day. The zone has to be supplied here, on the client.
//
// ⚠️ Only for values the BACKEND itself wrote (promo orders: `order_date` is set
// with `now()` in `OrderRepo`). Instore orders are the opposite case: the client
// sends its own wall clock (`InstoreBasketConfirm`), so that value is already
// local and must NOT pass through here — it would shift another hour or two.
//
// Done with `Intl` rather than a date library on purpose: luxon is in
// package.json but nothing imported it, and pulling it in for this one
// conversion cost 21 kB gzip. `Intl` is in the browser, knows CET/CEST from the
// system tz database, and costs nothing.

// A fixed zone, deliberately not the visitor's own: two people looking at the
// same order have to see the same hour, otherwise "what time does yours say?"
// is useless when supporting a shop.
const DISPLAY_ZONE = "Europe/Brussels";

const BACKEND_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})/;

const formatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: DISPLAY_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

interface DisplayParts {
  date: string;
  time: string;
}

function toDisplayZone(value: string): DisplayParts | null {
  const match = BACKEND_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [, day, month, year, hour, minute] = match;

  // Date.UTC is what makes the string UTC instead of the browser's own zone.
  const timestamp = Date.UTC(+year, +month - 1, +day, +hour, +minute);
  if (Number.isNaN(timestamp)) {
    return null;
  }

  const parts: Record<string, string> = {};
  for (const part of formatter.formatToParts(timestamp)) {
    parts[part.type] = part.value;
  }

  // Assembled by hand rather than via `format()`, so the output stays exactly
  // "dd/MM/yyyy HH:mm" regardless of how a locale likes to punctuate.
  return {
    date: `${parts.day}/${parts.month}/${parts.year}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

// Date + time, as shown in the order lists.
export function fromUtcBackendDateTime(value?: string, fallback: string = "") {
  if (!value) {
    return fallback;
  }
  const converted = toDisplayZone(value);

  // An unexpected format shouldn't turn into garbage on screen — show what the
  // backend sent, so the real problem stays visible.
  return converted ? `${converted.date} ${converted.time}` : value;
}

// Date only, for the places that used to drop the time with `.split(" ")[0]`.
// Converting first matters: without it, a late-night order lands on the wrong day.
export function fromUtcBackendDate(value?: string, fallback: string = "") {
  if (!value) {
    return fallback;
  }
  const converted = toDisplayZone(value);

  return converted ? converted.date : value.split(" ")[0];
}
